import React, { useState, useEffect, forwardRef, LegacyRef } from "react";

import { styled } from "@mui/material/styles";
import Button from "@mui/material/Button";
import DeleteIcon from "@mui/icons-material/Delete";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import Typography from "@mui/material/Typography";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import Box from "@mui/material/Box";
import { useDrag } from "react-dnd";

import { ConditionProps } from "./ConditionComponent";
import useConditions from "../../hooks/useConditions";
import { ConditionDragItem, draggable_types } from "../../helpers/DragHelper";
import { useRowSelector } from "../../store/Hooks";
import Condition from "../../models/conditions/Condition";
import { DatabaseTable } from "../../models/BaseTable";

const RuleContainer = styled("div")(({ theme }) => ({
  display: "flex",
}));

const RuleRow = styled("div")(({ theme }) => ({
  borderRadius: 5,
  background: "white",
}));

const RuleFields = styled("div")(({ theme }) => ({
  display: "flex",
  flexGrow: 1,
}));

const ReferenceText = styled(Typography)(({ theme }) => ({
  lineHeight: 2,
  marginLeft: "5px",
  fontStyle: "italic",
}));

const RuleDetails = styled("div")(({ theme }) => ({
  display: "flex",
  flexGrow: 1,
  flexWrap: "wrap",
}));

const DeleteRule = styled(Button)(({ theme }) => ({
  float: "right",
}));
const EditRule = styled(Button)(({ theme }) => ({
  float: "right",
}));

const ConditionReference = forwardRef(
  (props: ConditionProps, ref: LegacyRef<HTMLDivElement> | undefined) => {
    const condition = useRowSelector(Condition, props.condition_id);
    if (typeof condition === "undefined") {
      throw new Error("Unable to display a condition that does not exist");
    }
    const conditionID: number =
      condition.value === null ? -1 : parseInt(condition.value); 
    const parent = useRowSelector(Condition, condition.parent_condition_id as number);
    const draggable = condition.parent_condition_id !== null;
    const readonly = props.readonly;

    const [{ isDragging }, drag, preview] = useDrag<
      ConditionDragItem,
      void,
      { isDragging: boolean | undefined }
    >(
      () => ({
        type: draggable_types.Condition,
        canDrag: () => draggable,
        item: new ConditionDragItem(
          condition.id,
          props.index,
          condition.parent_condition_id,
        ),
        collect: (monitor) => ({
          isDragging: monitor.isDragging(),
        }),
      }),
      [condition],
    );

    const { openConditionsBuilder, getConditions } = useConditions();
    const [conditionText, setConditionText] = useState(condition.toString());
    useEffect(() => {
      const fetchConditions = async () => {
        let root: Condition | null = null;
        const conditions = await getConditions(Condition, conditionID);
        for (const newCondition of conditions.directConditions) {
          if (newCondition.id === conditionID) {
            root = newCondition;
          }
        }

        let name = "";
        if (root !== null) {
          if (root.name !== null && root.name.trim()) {
            name = root.name.trim();
          } else {
            name = root.toString();
          }
        }
        setConditionText(name);
      };
      fetchConditions();
    }, [getConditions, condition.value]);

    return (
      <RuleContainer ref={ref} sx={props.sx}>
        <RuleRow ref={preview}>
          <Box>
            <span ref={drag}>
              <DragHandleIcon
                sx={{
                  display: "block",
                  cursor: "grab",
                  marginLeft: "auto",
                  marginRight: "auto",
                }}
              />
            </span>
          </Box>
          <RuleFields>
            <RuleDetails>
              <ReferenceText>{conditionText}</ReferenceText>
            </RuleDetails>
            <EditRule
              onClick={() =>
                openConditionsBuilder(
                  Condition,
                  props.condition_id,
                  props.condition_id,
                  true,
                )
              }
              title="Edit condition set"
            >
              <ModeEditIcon />
            </EditRule>
            <DeleteRule
              onClick={() => {
                if (!readonly) {
                  if (typeof parent !== "undefined") {
                    parent.removeChild(condition as Condition);
                  } else {
                    throw new Error(
                      "Unable to delete Condition Reference without a parent",
                    );
                  }
                }
              }}
              disabled={readonly}
            >
              <DeleteIcon />
            </DeleteRule>
          </RuleFields>
        </RuleRow>
      </RuleContainer>
    );
  },
);
ConditionReference.displayName = "ConditionReference";

export default ConditionReference;
